import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Chip,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  Notifications,
  LocalPharmacy,
  ChevronRight,
  Circle,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/auth';

const DRAWER_WIDTH = 268;

const SIDEBAR_BG = '#0A1929';
const SIDEBAR_HOVER = 'rgba(255,255,255,0.07)';
const SIDEBAR_ACTIVE_BG = 'rgba(59,130,196,0.18)';
const SIDEBAR_ACTIVE_BORDER = '#3B82C4';

interface LayoutProps {
  children: React.ReactNode;
}

const getRoleLabel = (role?: UserRole | string) => {
  switch (role) {
    case UserRole.ADMIN: return 'Administrator';
    case UserRole.MANAGER: return 'Manager';
    case UserRole.PHARMACIST: return 'Pharmacist';
    case UserRole.PHARMACY_TECHNICIAN: return 'Pharmacy Technician';
    default: return role ?? 'Staff';
  }
};

const getRoleBadgeColor = (role?: UserRole | string) => {
  switch (role) {
    case UserRole.ADMIN: return '#C8102E';
    case UserRole.MANAGER: return '#F59E0B';
    case UserRole.PHARMACIST: return '#059669';
    case UserRole.PHARMACY_TECHNICIAN: return '#0284C7';
    default: return '#64748B';
  }
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { state: authState, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleNotifOpen = (event: React.MouseEvent<HTMLElement>) => setNotifAnchor(event.currentTarget);
  const handleNotifClose = () => setNotifAnchor(null);

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/login', { replace: true });
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const menuItems = [
    {
      text: 'Prescription Processing',
      icon: <LocalPharmacy fontSize="small" />,
      path: '/pharmacy/prescription-processing',
      show: true,
      section: 'pharmacy',
    },
  ];

  const sectionLabels: Record<string, string> = {
    main: 'Overview',
    pharmacy: 'Pharmacy',
    jobs: 'Workflows',
    admin: 'Administration',
  };

  const sections = Array.from(new Set(menuItems.map(i => i.section)));

  const drawer = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: SIDEBAR_BG,
        color: 'white',
      }}
    >
      {/* Brand header */}
      <Box
        onClick={() => handleNavClick('/pharmacy/prescription-processing')}
        sx={{
          px: 2.5,
          py: 2.5,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '10px',
            bgcolor: '#005EB8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 0 0 3px rgba(59,130,196,0.25)',
          }}
        >
          <LocalPharmacy sx={{ fontSize: 20, color: 'white' }} />
        </Box>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2, fontSize: '0.95rem' }}
          >
            AB Pharmacy
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem' }}>
            Assistant Platform
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', pt: 1.5, px: 1.5 }}>
        {sections.map((section) => {
          const items = menuItems.filter(i => i.section === section);
          return (
            <Box key={section} sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.3)',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  px: 1,
                  display: 'block',
                  mb: 0.5,
                }}
              >
                {sectionLabels[section]}
              </Typography>
              <List disablePadding>
                {items.map((item) => {
                  const isActive = location.pathname === item.path ||
                    (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
                  return (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
                      <ListItemButton
                        onClick={() => handleNavClick(item.path)}
                        sx={{
                          borderRadius: '8px',
                          px: 1.5,
                          py: 1,
                          position: 'relative',
                          bgcolor: isActive ? SIDEBAR_ACTIVE_BG : 'transparent',
                          borderLeft: isActive ? `3px solid ${SIDEBAR_ACTIVE_BORDER}` : '3px solid transparent',
                          '&:hover': {
                            bgcolor: isActive ? SIDEBAR_ACTIVE_BG : SIDEBAR_HOVER,
                          },
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 32,
                            color: isActive ? '#3B82C4' : 'rgba(255,255,255,0.5)',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? 'white' : 'rgba(255,255,255,0.65)',
                          }}
                        />
                        {isActive && (
                          <ChevronRight sx={{ fontSize: 16, color: '#3B82C4', ml: 'auto' }} />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>

      {/* User profile at bottom */}
      <Box
        sx={{
          borderTop: '1px solid rgba(255,255,255,0.07)',
          px: 2,
          py: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#005EB8',
              fontSize: '0.85rem',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {authState.user?.full_name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'white',
                fontWeight: 600,
                fontSize: '0.8rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {authState.user?.full_name}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Circle
                sx={{
                  fontSize: 7,
                  color: '#22C55E',
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.68rem' }}
              >
                {getRoleLabel(authState.user?.role)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  if (!authState.isAuthenticated) {
    return <>{children}</>;
  }

  const pageTitle = menuItems.find(
    i => location.pathname === i.path || (i.path !== '/dashboard' && location.pathname.startsWith(i.path))
  )?.text ?? 'Alberta Pharmacy';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
        }}
      >
        <Toolbar sx={{ minHeight: '60px !important', px: { xs: 2, sm: 3 } }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" noWrap sx={{ fontSize: '1rem', fontWeight: 700, color: 'text.primary' }}>
              {pageTitle}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {new Date().toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>

          <Tooltip title="Notifications">
            <IconButton onClick={handleNotifOpen} sx={{ color: 'text.secondary', mr: 0.5 }}>
              <Badge badgeContent={0} color="error">
                <Notifications fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={notifAnchor}
            open={Boolean(notifAnchor)}
            onClose={handleNotifClose}
            PaperProps={{ sx: { width: 300, mt: 1 } }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2">Notifications</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleNotifClose}>
              <Typography variant="body2" color="text.secondary">No new notifications</Typography>
            </MenuItem>
          </Menu>

          <Tooltip title={authState.user?.full_name ?? 'Profile'}>
            <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0.5 }}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: '#005EB8',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                }}
              >
                {authState.user?.full_name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{ sx: { width: 220, mt: 1 } }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>{authState.user?.full_name}</Typography>
              <Typography variant="caption" color="text.secondary">{authState.user?.email}</Typography>
              <Box mt={0.5}>
                <Chip
                  label={getRoleLabel(authState.user?.role)}
                  size="small"
                  sx={{
                    bgcolor: getRoleBadgeColor(authState.user?.role),
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    height: 20,
                  }}
                />
              </Box>
            </Box>
            <Divider />
            <MenuItem onClick={handleProfileMenuClose} sx={{ py: 1.25 }}>
              <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ py: 1.25, color: 'error.main' }}>
              <ListItemIcon><Logout fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Sign Out</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              border: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Toolbar sx={{ minHeight: '60px !important' }} />
        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto', width: '100%' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
